<script setup lang="ts">
import { MateType, MemberType } from "@/enums/mateEnums";
import api from "@/modules/api";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { useMateCommit } from "@/stores/mateCommit";
import { useNotification } from "@/stores/notification";
import { useUI } from "@/stores/ui";
import { MateEquipment } from "@/types/entity";
import { useName, usePreviewImg, useStorageSize } from "@/use/common";
import moment from "moment-timezone";
import { SwipeInstance } from "vant";
import { showConfirmDialog, showLoadingToast } from "vant";
import {
  computed,
  onActivated,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import { useI18n } from "vue-i18n";
import { RouteLocationRaw, useRouter } from "vue-router";
import { Mate } from "@/types/entity";
import { pollMateGenerationTask } from "@/views/Mate/Create/MateCreateTask";
import { memberRarityMap } from "@/types/user";

const ICON_PATH = "/img/icons";
const { t } = useI18n();
const user = useGlobalUser();
const notification = useNotification();
const commit = useMateCommit();

const router = useRouter();
const userStore = useGlobalUser();
const mateStore = useMate();
const uiStore = useUI();

const showSearchPopup = ref(false);

const otherFeatureList = computed<
  {
    icon: string;
    title: string;
    indicator?: number;
    callback: () => void;
  }[]
>(() => {
  let list = [
    {
      icon: "fluent--box-16-regular",
      title: t("interactive.goods"),
      callback: () => {
        router.push({ name: "missionEquipment" });
      },
    },
    {
      icon: "mingcute--book-3-line",
      title: t("interactive.task"),
      callback: () => {
        router.push({ name: "userTask" });
      },
    },
    {
      icon: "mingcute--book-4-line",
      title: t("setting.billing"),
      callback: () => {
        router.push({ name: "powerCube" });
      },
    },
    {
      icon: "mingcute--brightness-line",
      title: t("SidebarNavi.theme"),
      callback: () => {
        uiStore.showThemePicker = true;
      },
    },
    {
      icon: "mingcute--settings-3-line",
      title: t("SidebarNavi.setting"),
      callback: () => {
        router.push({ name: "setting" });
      },
    },
    // {
    //   icon: "iconify mingcute--report-line",
    //   title: t("Cell.report"),
    //   callback: () => {
    //     contactCustomerService();
    //   },
    // },
  ];

  if (!user.impersonating) {
    list.push({
      icon: "mingcute--exit-line",
      title: t("SidebarNavi.logout"),
      callback: () => {
        logout();
      },
    });
  } else {
    list.push({
      icon: "mingcute--exit-line",
      title: t("SidebarNavi.stopImpersonate"),
      callback: async () => {
        showLoadingToast({
          message: t("Message.loading"),
          duration: 0,
        });
        await api.deleteImpersonate();
        location.href = `${import.meta.env.VITE_API_URL}/nova`;
      },
    });
  }

  return list;
});

const creatorFeatureList = computed<
  {
    icon: string;
    name: string;
    text: string;
    needUserMate?: boolean;
  }[]
>(() => [
  {
    name: "mateWishHome",
    icon: "mingcute--magic-2-line",
    text: "mateWish.name",
  },
  {
    name: "interactive",
    icon: "mingcute--planet-line",
    text: "SidebarNavi.creation",
  },
  {
    name: "storyList",
    icon: "mingcute--quill-pen-ai-line",
    text: "Buttons.story",
    needUserMate: true,
  },
  {
    name: "roleplayHome",
    icon: "mingcute--question-line",
    text: "roleplay.title",
  },
  {
    name: "draw",
    icon: "mingcute--paint-brush-line",
    text: "Buttons.handDraw",
  },
  {
    name: "collectionList",
    icon: "mingcute--dot-grid-fill",
    text: "collection.name",
  },
]);

const onMateItemClicked = async (
  item: (typeof creatorFeatureList.value)[number]
) => {
  if (!user.isLogin) return user.openLoginModal("register");
  const mate = item.needUserMate ? mateStore.userMate : mateStore.currentMate!;

  if (mateStore.currentMate?.id !== mate.id)
    await mateStore.changeCurrentMate(mate.id);
  // 2. 当前路由允许Mate切换：直接切换
  router.push({ name: item.name });
};

const initRewardCount = () => {
  if (!user.user.dtLastClaimChest) {
    canReceiveChest.value = true;
  } else {
    allSeconds =
      moment(user.user.dtLastClaimChest)
        .add(1, "day")
        .diff(moment(), "second") + 2;
    if (allSeconds <= 0) {
      canReceiveChest.value = true;
    } else {
      startCountDown();
    }
  }
};

const refreshNotification = () => {
  notification.refreshUnreadCount();
  commit.getPendingList();
  initRewardCount();
};

const canReceiveChest = ref(false);

let countDownTimer: ReturnType<typeof setInterval> | null = null;
let allSeconds = 0;
const countDown = ref<{
  hours: number;
  minutes: number;
  seconds: number;
}>();

const stopCountDown = () => {
  if (countDownTimer) {
    clearInterval(countDownTimer);
    countDownTimer = null;
    countDown.value = undefined;
  }
};

onBeforeUnmount(() => {
  stopCountDown();
});

const initCountDown = () => {
  const hours = Math.floor(allSeconds / 3600);
  const minutes = Math.floor((allSeconds % 3600) / 60);
  const seconds = allSeconds % 60;
  countDown.value = {
    hours,
    minutes,
    seconds,
  };
};

const startCountDown = () => {
  stopCountDown();

  countDownTimer = setInterval(() => {
    allSeconds--;

    if (allSeconds <= 0) {
      stopCountDown();
      canReceiveChest.value = true;
      return;
    }

    initCountDown();
  }, 1000);
  initCountDown();
};
const logout = async () => {
  await api.signOut();
  userStore.logout();
};

const showEquipmentModal = ref(false);
const activeEquipment = ref<MateEquipment>();

// 我的伙伴 section variables
const swiper = ref<SwipeInstance>();
const lineMateCount = ref(10);
const viewMode = ref<"avatar" | "portrait">("avatar");
const showOCSettings = ref(false);
const portraitPlaceholderHeight = ref(0);

// 我的伙伴 section functions
const clickViewMode = (mode: "avatar" | "portrait") => {
  viewMode.value = mode;
  userStore.updateUserExtraConfig({ viewMode: mode });
};

const onLineMateCountChange = (value: number) => {
  userStore.updateUserExtraConfig({ lineMateCount: value });
  lineMateCount.value = value;
};

const onMateNavClicked = async (to: RouteLocationRaw) => {
  if (!user.isLogin) return user.openLoginModal("register");
  router.push(to);
};

const getPortraitUrl = (mate: Mate) => {
  if (!mate.spritezz?.length) return "";

  let defaultSprite = mate.spritezz?.find((v) => v.isDefault);
  if (!defaultSprite) defaultSprite = mate.spritezz![0];
  if (!defaultSprite.portraitMedia?.url) return "";

  return usePreviewImg(defaultSprite!.portraitMedia!.url);
};

let startPosition = { x: 0, y: 0 };
const onMouseDown = (event: MouseEvent) => {
  startPosition = { x: event.clientX, y: event.clientY };
};

const onMouseUp = (event: MouseEvent, mate?: Mate) => {
  const distance = {
    x: event.clientX - startPosition.x,
    y: event.clientY - startPosition.y,
  };

  if (Math.abs(distance.x) < 10 && Math.abs(distance.y) < 10) {
    mate
      ? onMateNavClicked({
          name: "mateEdit",
          params: { uuid: mate.uuid },
        })
      : mateStore.clickCreateMateOption();
  }
};

const initUserIfHasTask = async () => {
  await user.refresh();

  if (user.user.processingMateGenerationTaskIds?.length) {
    user.user.processingMateGenerationTaskIds.forEach((taskId) => {
      pollMateGenerationTask(taskId);
    });
  }
};

const clickCreateSelf = async () => {
  const loading = showLoadingToast(t(`Message.loading`));
  const { item } = await api.createOneMate({ type: MateType.SelfAvatar });
  await mateStore.fetchSelfAvatarList();
  loading.close();
  mateStore.changeCurrentMate(item.id);
  return item;
};

const modalStore = useGlobalModal();
const clickCreateMate = async () => {
  modalStore.openCustomGlobalActionSheet({
    title: t("Message.howToGetSelfAvatar"),
    list: [
      {
        content:
          "<i class='iconify pixelarticons--human-handsdown'></i> " +
          t("MineMate.mode.preset"),
        className: "btn-primary",
        callback: async () => {
          if (mateStore.selfAvatarList.some((v) => !v.name)) {
            try {
              await showConfirmDialog({
                message: t("Message.unNamedSelfAvatar"),
                confirmButtonText: t("Buttons.confirm"),
                cancelButtonText: t("Buttons.cancel"),
              });
            } catch {
              return;
            }
          }
          await clickCreateSelf();
          router.push({
            name: "mateCreate",
            query: {
              isPreset: 1,
              mateType: MateType.SelfAvatar,
            },
          });
        },
      },
      {
        content:
          "<i class='iconify mingcute--photo-album-line'></i> " +
          t("MineMate.mode.reference"),
        callback: async () => {
          if (mateStore.selfAvatarList.some((v) => !v.name)) {
            try {
              await showConfirmDialog({
                message: t("Message.unNamedSelfAvatar"),
                confirmButtonText: t("Buttons.confirm"),
                cancelButtonText: t("Buttons.cancel"),
              });
            } catch {
              return;
            }
          }
          await clickCreateSelf();
          router.push({
            name: "mateCreate",
            query: {
              isImage: 1,
            },
          });
        },
      },
      {
        content:
          "<i class='iconify mingcute--grid-line'></i> " +
          t("MineMate.mode.selfEditor"),
        callback: async () => {
          if (mateStore.selfAvatarList.some((v) => !v.name)) {
            try {
              await showConfirmDialog({
                message: t("Message.unNamedSelfAvatar"),
                confirmButtonText: t("Buttons.confirm"),
                cancelButtonText: t("Buttons.cancel"),
              });
            } catch {
              return;
            }
            const mate = await clickCreateSelf();
            router.push({
              name: "treeholeSelfSetting",
              query: {
                mateUuid: mate.uuid,
              },
            });
          }
        },
      },
    ],
  });
};

onActivated(() => {
  refreshNotification();
  mateStore.getMateList(true);
  initUserIfHasTask();
});

onMounted(() => {
  refreshNotification();

  // Initialize 我的伙伴 section
  const _lineMateCount = userStore.user.extraConfig?.lineMateCount;
  const _viewMode = userStore.user.extraConfig?.viewMode;

  if (_viewMode) {
    viewMode.value = _viewMode as "avatar" | "portrait";
  }

  if (_lineMateCount) {
    lineMateCount.value = _lineMateCount as number;
  }

  let width = document.body.clientWidth;
  if (width > 1024) width = 600;

  portraitPlaceholderHeight.value = (width / 3) * 4;
  initUserIfHasTask();
});

onBeforeMount(() => {
  mateStore.getMateList(true);
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_auto_auto_1fr]">
    <ThreadsNav :show-center="false">
      <template #right>
        <AddQQGroupBtn />
        <button
          class="btn btn-sm btn-ghost indicator"
          @click="
            router.push({
              name: 'notificationList',
            })
          "
        >
          <i class="iconify w-6 h-6 ic--outline-notifications-active"></i>
          <span
            v-if="notification.totalUnreadNotificationCount"
            class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-4 h-4 right-2 top-2"
          >
            {{ notification.totalUnreadNotificationCount }}
          </span>
        </button>
      </template>
    </ThreadsNav>
    <div
      class="flex p-4 m-2 gap-4 items-center cursor-pointer bg-base-200 rounded-xl"
    >
      <div class="flex flex-col flex-1 text-base">
        <div
          class="font-bold flex justify-between"
          @click="
            router.push({
              name: 'mateHome',
              params: {
                uuid: mateStore.userMate?.uuid,
              },
            })
          "
        >
          <span>
            {{ mateStore.userMate?.name }}
          </span>

          <div>
            <button class="btn-ghost">
              <i class="iconify mingcute--right-line"></i>
            </button>
          </div>
        </div>

        <div class="flex justify-between items-center">
          <div class="flex text-base-content/70 text-xs leading-snug gap-1">
            <div
              v-if="userStore.getMemberType !== MemberType.Free"
              :class="[
                'flex items-center gap-1 border rounded-full px-1  text-xs',
                memberRarityMap[userStore.getMemberType],
              ]"
              @click.stop="router.push({ name: 'memberCenter' })"
            >
              <span>会员: </span>
              <span class="font-medium">
                {{ $t(`User.memberType.${userStore.getMemberType}`) }}
              </span>
            </div>
            <div v-else class="flex gap-2 w-full">
              <button
                class="btn btn-xs btn-primary"
                @click.stop="router.push({ name: 'member' })"
              >
                会员中心
              </button>

              <!-- <div class="flex items-center gap-1 opacity-60">
              开通会员, 实现
              <PowerIcon />
              自由！
            </div> -->
            </div>
          </div>

          <div
            class="flex text-base-content/70 text-xs leading-snug gap-1"
            v-if="userStore.user.storageUsage"
          >
            <div
              :class="[
                'flex items-center gap-1 border rounded-md px-1  text-xs border-base-content/30',
              ]"
              @click.stop="router.push({ name: 'gallery' })"
            >
              空间: {{ useStorageSize(userStore.user.storageUsage.usedSize) }}
              /
              {{ useStorageSize(userStore.user.storageUsage.totalSize) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="h-full overflow-y-auto pl-4 pr-2 gap-2 flex flex-col pt-3 pb-2">
      <!-- 我的自设 -->
      <div class="flex justify-between items-center">
        <div class="text-base font-semibold red-dot">
          {{ $t("User.mate.mySetting") }}
        </div>

        <button class="btn btn-xs" @click="clickCreateMate">+自设</button>
      </div>
      <div class="overflow-x-auto flex gap-4 items-center flex-shrink-0">
        <div
          class="flex flex-col gap-2 w-12 flex-shrink-0 items-center relative"
          @click="
            router.push({
              name: 'treeholeSelfSetting',
              query: {
                mateUuid: mateStore.userMate.uuid,
              },
            })
          "
        >
          <UserAvatar :mate="mateStore.userMate" class="w-full" />
          <p class="text-xs line-clamp-1">
            {{ useName(mateStore.userMate.name) }}
          </p>
        </div>
        <div
          v-for="mate in mateStore.selfAvatarList"
          :key="mate.id"
          class="flex flex-col gap-2 w-12 flex-shrink-0 items-center relative"
          @click="
            router.push({
              name: 'treeholeSelfSetting',
              query: {
                mateUuid: mate.uuid,
              },
            })
          "
        >
          <UserAvatar :mate="mate" class="w-full" />
          <p class="text-xs line-clamp-1">
            {{ useName(mate.name) }}
          </p>
        </div>
      </div>

      <!-- 我的伙伴 -->
      <div class="flex justify-between items-center shrink-0">
        <div class="text-base font-semibold red-dot">我的伙伴</div>
        <div class="flex items-center gap-2">
          <button
            class="btn btn-xs rounded"
            @click="onMateNavClicked({ name: 'gallery' })"
          >
            <i class="iconify mingcute--photo-album-line"></i>
            {{ $t(`User.mate.module.gallery`) }}
          </button>

          <button
            class="btn btn-xs"
            @click="
              user.isLogin
                ? clickViewMode(viewMode === 'avatar' ? 'portrait' : 'avatar')
                : user.openLoginModal('register')
            "
          >
            <i
              :class="[
                'iconify',
                viewMode === 'avatar'
                  ? 'pixelarticons--human-handsdown'
                  : 'mingcute--grid-line',
              ]"
            ></i>
            {{
              $t(
                `User.mate.viewMode.${
                  viewMode === "portrait" ? "avatar" : "portrait"
                }`
              )
            }}
          </button>

          <button
            class="btn btn-xs"
            v-if="viewMode === 'avatar'"
            @click="
              user.isLogin
                ? (showOCSettings = true)
                : user.openLoginModal('register')
            "
          >
            <i class="iconify mingcute--grid-line"></i>
          </button>
        </div>
      </div>

      <div class="overflow-hidden flex flex-col shrink-0">
        <div>
          <div
            v-for="mate in mateStore.list"
            :key="mate.id"
            class="flex flex-col gap-1 flex-shrink-0 items-center relative overflow-hidden"
            @click="
              onMateNavClicked({
                name: 'mateEdit',
                params: { uuid: mate.uuid },
              })
            "
          ></div>
        </div>
        <!-- 立绘模式 -->
        <VanSwipe
          ref="swiper"
          v-if="viewMode === 'portrait'"
          class="relative my-4"
        >
          <VanSwipeItem v-for="mate in mateStore.list" :key="mate.id">
            <img
              v-if="getPortraitUrl(mate)"
              :src="getPortraitUrl(mate)"
              alt=""
              @dragstart.prevent
              @mousedown="onMouseDown"
              @mouseup="onMouseUp($event, mate)"
              class="w-full h-full max-h-[70vh] object-contain"
            />

            <div
              v-else
              class="flex justify-center max-h-[70vh] overflow-hidden"
              @dragstart.prevent
              @mousedown="onMouseDown"
              @mouseup="onMouseUp($event, mate)"
            >
              <div class="">
                <ImgPortraitPlaceholder
                  :style="{ height: portraitPlaceholderHeight }"
                  class="text-secondary"
                />
              </div>
            </div>
            <div class="text-xs text-center mt-2">
              {{ useName(mate.name) }}
            </div>
          </VanSwipeItem>

          <template v-if="user.user.processingMateGenerationTaskIds?.length">
            <VanSwipeItem
              v-for="taskId in user.user.processingMateGenerationTaskIds"
              :key="taskId"
            >
              <div
                class="flex justify-center max-h-[70vh] overflow-hidden"
                @dragstart.prevent
              >
                <div class="relative">
                  <ImgPortraitPlaceholder
                    :style="{ height: portraitPlaceholderHeight }"
                    class="text-secondary opacity-30"
                  />

                  <MateCreateImgLoading
                    :allLoading="true"
                    :singleLoading="true"
                    :singlePercent="0"
                    :needWait="false"
                    :isFrameGenerating="true"
                  />
                </div>
              </div>
              <div class="text-xs text-center mt-2">努力生成中...</div>
            </VanSwipeItem>
          </template>

          <VanSwipeItem :key="0">
            <div
              class="flex flex-col gap-1 w-full"
              @mousedown="onMouseDown"
              @mouseup="onMouseUp($event)"
            >
              <div
                class="aspect-[3/4] border-primary-content border-dashed rounded-lg flex justify-center items-center bg-neutral max-h-[70vh]"
              >
                <div
                  class="w-[40%] aspect-[3/4] overflow-hidden flex items-center justify-center border border-dashed rounded-lg border-neutral-content"
                >
                  <i
                    class="iconify mingcute--add-line text-neutral-content font-light w-[100%]"
                  />
                </div>
              </div>
              <p class="text-xs text-center">{{ $t(`Buttons.add`) }}</p>
            </div>
          </VanSwipeItem>
          <template #indicator="{ active, total }">
            <div
              class="flex items-center justify-center w-10 h-10 absolute left-2 top-1/2 -translate-y-1/2 bg-base-100/80 backdrop-blur-sm rounded-full shadow-lg hover:bg-base-100/90 transition-all duration-200 cursor-pointer group"
              @click="swiper?.prev()"
            >
              <i
                class="iconify mingcute--left-line text-xl text-base-content/70 group-hover:text-base-content transition-colors"
              ></i>
            </div>
            <div
              class="flex items-center justify-center w-10 h-10 absolute right-2 top-1/2 -translate-y-1/2 bg-base-100/80 backdrop-blur-sm rounded-full shadow-lg hover:bg-base-100/90 transition-all duration-200 cursor-pointer group"
              @click="swiper?.next()"
            >
              <i
                class="iconify mingcute--right-line text-xl text-base-content/70 group-hover:text-base-content transition-colors"
              ></i>
            </div>

            <div
              class="absolute right-1 bottom-1 px-2 py-1 text-xs bg-black/10 rounded-full"
            >
              {{ active + 1 }}/{{ total }}
            </div>
          </template>
        </VanSwipe>

        <!-- 列表模式 -->
        <div
          v-else
          :class="[`grid gap-1 items-center flex-1 pr-2`]"
          :style="`grid-template-columns: repeat(${lineMateCount}, 1fr);`"
          :key="lineMateCount"
        >
          <div
            class="flex flex-col gap-1 w-full"
            @click="mateStore.clickCreateMateOption()"
          >
            <div
              class="aspect-[3/4] border-primary-content border-dashed rounded-lg flex justify-center items-center bg-neutral h-full"
            >
              <div
                class="w-[40%] aspect-square overflow-hidden flex items-center justify-center border border-dashed rounded-full border-neutral-content"
              >
                <i
                  class="iconify mingcute--add-line text-neutral-content font-light w-[80%]"
                />
              </div>
            </div>
            <p class="text-xs text-center">{{ $t(`Buttons.add`) }}</p>
          </div>
          <div
            v-for="mate in mateStore.list"
            :key="mate.id"
            class="flex flex-col gap-1 flex-shrink-0 items-center relative overflow-hidden"
            @click="
              onMateNavClicked({
                name: 'mateEdit',
                params: { uuid: mate.uuid },
              })
            "
          >
            <div
              v-if="mate.isPreset"
              class="absolute top-0 right-0 badge badge-neutral z-[1] badge-xs text-[10px]"
            >
              {{ $t(`User.mate.preset`) }}
            </div>

            <div
              v-else-if="mate.status === 0"
              class="absolute top-0 right-0 badge badge-neutral z-[1] badge-xs text-[10px]"
            >
              {{ $t(`User.mate.draft`) }}
            </div>
            <UserAvatar :mate="mate" class="w-full" />
            <p class="text-xs line-clamp-1 w-full text-center">
              {{ useName(mate.name) }}
            </p>
          </div>
          <template v-if="user.user.processingMateGenerationTaskIds?.length">
            <div
              v-for="taskId in user.user.processingMateGenerationTaskIds"
              :key="taskId"
              class="flex flex-col gap-1 flex-shrink-0 items-center relative overflow-hidden"
            >
              <div class="w-full relative">
                <ImgAvatarPlaceholder
                  class="text-secondary opacity-30 aspect-[3/4]"
                />
                <MateCreateImgLoading
                  :allLoading="true"
                  :singleLoading="true"
                  :singlePercent="0"
                  :needWait="false"
                  :isFrameGenerating="true"
                  size="loading-md"
                />
              </div>
              <p class="text-xs line-clamp-1 w-full text-center">生成中...</p>
            </div>
          </template>
        </div>
      </div>
      <!-- 奖励 -->
      <div class="flex justify-between items-center">
        <div class="text-base font-semibold red-dot indicator">
          <span
            class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-5 h-5"
            v-if="user.rewardCount"
          >
            {{ user.rewardCount }}
          </span>
          {{ $t("mission.reward") }}
        </div>

        <button class="btn btn-xs" @click="router.push(`/crystal#3`)">
          {{ $t(`purchase.name`) }}
        </button>
      </div>
      <MineClaim />

      <RedotTitle :title="$t('Home.advancedCreate')" />
      <div
        class="grid grid-cols-6 justify-between md:justify-start md:gap-8 py-4 px-1 bg-base-200 rounded-xl flex-wrap items-center gap-y-4"
      >
        <div
          v-for="item in creatorFeatureList"
          :key="item.icon"
          class="flex flex-col gap-1 cursor-pointer items-center justify-center"
          @click="onMateItemClicked(item)"
        >
          <i :class="item.icon" class="iconify" />
          <div class="text-center text-xs">{{ $t(item.text) }}</div>
        </div>
      </div>
      <RedotTitle :title="$t('Titles.others')" />
      <div
        class="grid grid-cols-4 justify-between md:justify-start md:gap-8 py-4 px-1 bg-base-200 rounded-xl flex-wrap items-center gap-y-4"
      >
        <div class="w-full indicator flex justify-center items-center">
          <span
            class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-5 h-5"
            v-if="commit.pendingCount"
          >
            {{ commit.pendingCount }}
          </span>
          <RouterLink
            class="flex flex-col gap-1 cursor-pointer items-center justify-center"
            :to="{ name: 'friendList' }"
          >
            <div class="rounded-xl overflow-hidden">
              <img :src="`${ICON_PATH}/friends.png`" alt="" class="w-[21px]" />
            </div>
            <div class="text-center text-xs">{{ $t("Titles.friends") }}</div>
          </RouterLink>
        </div>
        <div>
          <RouterLink
            class="flex flex-col gap-1 cursor-pointer items-center justify-center"
            :to="{ name: 'friendInvitation' }"
          >
            <div class="rounded-xl overflow-hidden">
              <img :src="`${ICON_PATH}/invite.png`" alt="" class="w-[21px]" />
            </div>
            <div class="text-center text-xs">
              {{ $t("Titles.inviteFriends") }}
            </div>
          </RouterLink>
        </div>
        <div
          v-for="item in otherFeatureList"
          :key="item.icon"
          :class="{ indicator: item.indicator }"
        >
          <span
            class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-5 h-5"
            v-if="item.indicator"
          >
            {{ item.indicator }}
          </span>
          <div
            class="flex flex-col gap-1 cursor-pointer items-center justify-center"
            @click="item.callback()"
          >
            <i :class="item.icon" class="iconify" />
            <div class="text-center text-xs">{{ item.title }}</div>
          </div>
        </div>
      </div>
    </div>
    <SearchPopup v-model:show="showSearchPopup" />

    <EquipmentDetailModal
      v-model:show="showEquipmentModal"
      :equipment="activeEquipment"
      mode="edit"
      show-gain-title
    />
    <!-- Settings Popup -->
    <VanPopup v-model:show="showOCSettings" position="top">
      <div class="p-4 flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <div>一行展示几个伙伴？</div>
          <div class="flex items-center justify-between gap-5">
            <div>3</div>
            <van-slider
              v-model="lineMateCount"
              @change="onLineMateCountChange"
              :min="3"
              :max="10"
              :step="1"
              :disabled="viewMode === 'portrait'"
            >
              <template #button>
                <div class="btn btn-xs btn-primary">{{ lineMateCount }}</div>
              </template>
            </van-slider>
            <div>10</div>
          </div>
        </div>
      </div>
    </VanPopup>
  </div>
</template>
